import React from 'react';
import { 
  Building, Users, CreditCard, Palette, Zap, Workflow, Shield, 
  Database, Activity, Settings, Plus, Edit3, Trash2, Eye, EyeOff,
  Copy, Check, AlertTriangle, Info, ExternalLink, Globe,
  Key, Lock, Code, Bell, Mail, Cloud, Save, RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Tenant Management Section
export const TenantManagement = ({ 
  tenants, 
  newTenant, 
  setNewTenant, 
  setShowModal, 
  setModalType, 
  setSelectedTenant 
}) => (
  <div className="space-y-6">
    {/* Tenants Overview */}
    <div className="bg-white rounded-xl shadow-md">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Active Tenants</h2>
          <button
            onClick={() => {
              setModalType('create-tenant');
              setShowModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Add Tenant</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <div key={tenant.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                  <p className="text-sm text-gray-600">{tenant.subdomain}.junotalent.com</p>
                  {tenant.customDomain && (
                    <p className="text-sm text-blue-600">{tenant.customDomain}</p>
                  )}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                  tenant.status === 'suspended' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {tenant.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium capitalize">{tenant.plan}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Users:</span>
                  <span className="font-medium">{tenant.users}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Jobs:</span>
                  <span className="font-medium">{tenant.jobs}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Storage:</span>
                  <span className="font-medium">{tenant.storageUsed} MB</span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setModalType('edit-tenant');
                    setShowModal(true);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                >
                  <Edit3 className="h-3 w-3 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setSelectedTenant(tenant);
                    setModalType('tenant-details');
                    setShowModal(true);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                >
                  <Eye className="h-3 w-3 inline mr-1" />
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Quick Stats */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-3">
          <Building className="h-8 w-8 text-blue-600" />
          <div>
            <p className="text-sm text-gray-600">Total Tenants</p>
            <p className="text-2xl font-bold text-gray-900">{tenants.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-green-600" />
          <div>
            <p className="text-sm text-gray-600">Total Users</p>
            <p className="text-2xl font-bold text-gray-900">
              {tenants.reduce((sum, t) => sum + t.users, 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-3">
          <Activity className="h-8 w-8 text-purple-600" />
          <div>
            <p className="text-sm text-gray-600">Active Tenants</p>
            <p className="text-2xl font-bold text-gray-900">
              {tenants.filter(t => t.status === 'active').length}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center space-x-3">
          <Database className="h-8 w-8 text-orange-600" />
          <div>
            <p className="text-sm text-gray-600">Total Storage</p>
            <p className="text-2xl font-bold text-gray-900">
              {(tenants.reduce((sum, t) => sum + t.storageUsed, 0) / 1024).toFixed(1)} GB
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Authentication & Users Section
export const AuthenticationUsers = ({ systemConfig, setSystemConfig }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Authentication Configuration</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Authentication Provider */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Authentication Provider
          </label>
          <select
            value={systemConfig.authProvider}
            onChange={(e) => setSystemConfig({...systemConfig, authProvider: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="internal">Internal Authentication</option>
            <option value="oauth">OAuth (Google, Microsoft)</option>
            <option value="saml">SAML SSO</option>
            <option value="ldap">LDAP/Active Directory</option>
          </select>
        </div>

        {/* Default Plan */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Plan for New Tenants
          </label>
          <select
            value={systemConfig.defaultPlan}
            onChange={(e) => setSystemConfig({...systemConfig, defaultPlan: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="starter">Starter Plan</option>
            <option value="professional">Professional Plan</option>
            <option value="enterprise">Enterprise Plan</option>
          </select>
        </div>

        {/* Trial Configuration */}
        <div>
          <div className="flex items-center space-x-2 mb-2">
            <input
              type="checkbox"
              id="enableTrials"
              checked={systemConfig.enableTrials}
              onChange={(e) => setSystemConfig({...systemConfig, enableTrials: e.target.checked})}
              className="rounded"
            />
            <label htmlFor="enableTrials" className="text-sm font-medium text-gray-700">
              Enable Free Trials
            </label>
          </div>
          {systemConfig.enableTrials && (
            <input
              type="number"
              value={systemConfig.trialDays}
              onChange={(e) => setSystemConfig({...systemConfig, trialDays: parseInt(e.target.value)})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Trial days"
              min="1"
              max="90"
            />
          )}
        </div>

        {/* Self-Registration */}
        <div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enableSignup"
              checked={systemConfig.enableSignup}
              onChange={(e) => setSystemConfig({...systemConfig, enableSignup: e.target.checked})}
              className="rounded"
            />
            <label htmlFor="enableSignup" className="text-sm font-medium text-gray-700">
              Enable Self-Registration
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1">Allow new organizations to sign up automatically</p>
        </div>
      </div>
    </div>

    {/* User Management Features */}
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">User Management Features</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Role-Based Access</h4>
          <p className="text-sm text-gray-600 mb-3">Hierarchical permissions per tenant</p>
          <div className="space-y-2">
            <div className="text-xs">✓ Tenant Admin</div>
            <div className="text-xs">✓ HR Manager</div>
            <div className="text-xs">✓ Recruiter</div>
            <div className="text-xs">✓ Viewer</div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Multi-Factor Auth</h4>
          <p className="text-sm text-gray-600 mb-3">Enhanced security options</p>
          <div className="space-y-2">
            <div className="text-xs">✓ SMS/Email OTP</div>
            <div className="text-xs">✓ Authenticator Apps</div>
            <div className="text-xs">✓ Hardware Tokens</div>
            <div className="text-xs">✓ Biometric Auth</div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Session Management</h4>
          <p className="text-sm text-gray-600 mb-3">Control user sessions</p>
          <div className="space-y-2">
            <div className="text-xs">✓ Timeout Control</div>
            <div className="text-xs">✓ Concurrent Sessions</div>
            <div className="text-xs">✓ Force Logout</div>
            <div className="text-xs">✓ Activity Monitoring</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Billing & Subscriptions Section
export const BillingSubscriptions = ({ systemConfig, setSystemConfig }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">Billing Configuration</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Billing Provider */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Billing Provider
          </label>
          <select
            value={systemConfig.billingProvider}
            onChange={(e) => setSystemConfig({...systemConfig, billingProvider: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
            <option value="chargebee">Chargebee</option>
            <option value="manual">Manual/Invoicing</option>
          </select>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Currency
          </label>
          <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="CAD">CAD - Canadian Dollar</option>
          </select>
        </div>
      </div>
    </div>

    {/* Subscription Plans */}
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Plans</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Starter Plan */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">Starter</h4>
            <span className="text-lg font-bold text-green-600">$49/mo</span>
          </div>
          <div className="space-y-2 text-sm">
            <div>✓ Up to 5 users</div>
            <div>✓ 50 job postings</div>
            <div>✓ 1GB storage</div>
            <div>✓ Basic integrations</div>
            <div>✓ Email support</div>
          </div>
          <button className="w-full mt-4 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Edit Plan
          </button>
        </div>

        {/* Professional Plan */}
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">Professional</h4>
            <span className="text-lg font-bold text-blue-600">$149/mo</span>
          </div>
          <div className="space-y-2 text-sm">
            <div>✓ Up to 25 users</div>
            <div>✓ 500 job postings</div>
            <div>✓ 10GB storage</div>
            <div>✓ All integrations</div>
            <div>✓ AI sourcing</div>
            <div>✓ Phone support</div>
          </div>
          <button className="w-full mt-4 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Edit Plan
          </button>
        </div>

        {/* Enterprise Plan */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">Enterprise</h4>
            <span className="text-lg font-bold text-purple-600">Custom</span>
          </div>
          <div className="space-y-2 text-sm">
            <div>✓ Unlimited users</div>
            <div>✓ Unlimited jobs</div>
            <div>✓ 100GB+ storage</div>
            <div>✓ Custom integrations</div>
            <div>✓ White-label</div>
            <div>✓ Dedicated support</div>
          </div>
          <button className="w-full mt-4 px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
            Edit Plan
          </button>
        </div>
      </div>
    </div>

    {/* Usage Monitoring */}
    <div className="bg-white rounded-xl shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Monitoring & Limits</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Resource Limits</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Max Users per Tenant</span>
              <input
                type="number"
                value={systemConfig.globalResourceLimits.maxUsersPerTenant}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                readOnly
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Max Jobs per Tenant</span>
              <input
                type="number"
                value={systemConfig.globalResourceLimits.maxJobsPerTenant}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                readOnly
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Max Storage per Tenant (MB)</span>
              <input
                type="number"
                value={systemConfig.globalResourceLimits.maxStoragePerTenant}
                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                readOnly
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Billing Automation</h4>
          <div className="space-y-3">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Automatic billing</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Usage overage alerts</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" className="rounded" />
              <span className="text-sm">Grace period for overages</span>
            </label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked className="rounded" />
              <span className="text-sm">Suspend on payment failure</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default {
  TenantManagement,
  AuthenticationUsers,
  BillingSubscriptions
};